import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, TrendingUp, Sparkles, Award, Clock, ShieldCheck, Search, MessageSquare, CreditCard, Star, Store } from "lucide-react";
import Layout from "@/components/Layout";
import CategoryBar from "@/components/CategoryBar";
import VendorCard from "@/components/VendorCard";
import EnquiryDialog from "@/components/EnquiryDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useCategoryTheme } from "@/context/CategoryThemeContext";
import { useLocationCtx } from "@/context/LocationContext";
import api from "@/lib/api";

function PopularCategories({ categories }) {
  const navigate = useNavigate();
  const scroller = useRef(null);

  const popularCategories = (categories || [])
    .filter((c) => !c.is_all && c.banner)
    .slice(0, 10);

  useEffect(() => {
    const el = scroller.current;
    if (!el || popularCategories.length <= 1) return;

    const timer = setInterval(() => {
      const firstCard = el.querySelector("[data-category-slide]");
      if (!firstCard) return;

      const cardWidth = firstCard.getBoundingClientRect().width;
      const gap = 10;
      const maxScroll = el.scrollWidth - el.clientWidth;

      if (el.scrollLeft + cardWidth + gap >= maxScroll - 4) {
        el.scrollTo({
          left: 0,
          behavior: "smooth",
        });
      } else {
        el.scrollBy({
          left: cardWidth + gap,
          behavior: "smooth",
        });
      }
    }, 5500);

    return () => clearInterval(timer);
  }, [popularCategories.length]);

  if (!popularCategories.length) return null;

  return (
    <section
      className="w-full px-3 sm:px-6 lg:px-8 pt-2 pb-3 sm:py-6"
      data-testid="popular-categories"
    >
      <div className="max-w-7xl mx-auto">
        <h2 className="font-display font-bold text-lg sm:text-2xl mb-3">
          Popular Categories
        </h2>

        <div
          ref={scroller}
          className="flex gap-2.5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-1"
          style={{ scrollBehavior: "smooth" }}
        >
          {popularCategories.map((c) => (
            <motion.button
              key={c.slug}
              data-category-slide
              onClick={() => navigate(`/category/${c.slug}`)}
              className="relative shrink-0 w-[128px] sm:w-[180px] aspect-[4/3] rounded-2xl overflow-hidden bg-muted shadow-[0_4px_14px_rgba(0,0,0,0.12)] snap-start group"
              whileTap={{ scale: 0.98 }}
            >
              <img
                src={c.banner}
                alt={c.name}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />

              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(to top, ${c.theme?.accent || "#111"}dd, transparent 65%)`,
                }}
              />

              <div className="absolute bottom-0 left-0 right-0 p-3 text-white text-left">
                <div className="font-display font-bold text-[11px] sm:text-base leading-tight">
                  {c.name}
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}

function Row({ title, icon: Icon, vendors, onEnquire, testid }) {
  const scroller = useRef(null);
  const navigate = useNavigate();
  const isFeaturedRow = testid === "row-featured";
  const isTrendingRow = testid === "row-trending";
  const [autoIndex, setAutoIndex] = useState(0);

  useEffect(() => {
    if (!isFeaturedRow || !vendors?.length || vendors.length <= 1) return;

    const timer = setInterval(() => {
      setAutoIndex((current) => (current + 1) % vendors.length);
    }, 4500);

    return () => clearInterval(timer);
  }, [isFeaturedRow, vendors?.length]);

  useEffect(() => {
    const el = scroller.current;
    if (!isFeaturedRow || !el || !vendors?.length) return;

    const isMobile = window.innerWidth < 640;
    if (!isMobile) return;

    const firstCard = el.querySelector("[data-vendor-slide]");
    if (!firstCard) return;

    const cardWidth = firstCard.getBoundingClientRect().width;
    const gap = 8;

    el.scrollTo({
      left: autoIndex * (cardWidth + gap),
      behavior: "smooth",
    });
  }, [autoIndex, vendors?.length]);

  if (!vendors?.length) return null;

  return (
    <section
      className={`w-full px-1.5 sm:px-2 ${
        isTrendingRow ? "pt-0 pb-0" : "pt-1 pb-0"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2 px-0">
        <h2 className="font-display font-bold text-lg sm:text-2xl flex items-center gap-2">
          {Icon && <Icon className="h-5 w-5 text-purple-600" />} {title}
        </h2>

        <button
          onClick={() => navigate("/search")}
          className="text-xs sm:text-sm font-semibold text-purple-600 flex items-center gap-1 hover:gap-1.5 transition-all"
        >
          View all <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div
        ref={scroller}
        className="flex gap-2 overflow-x-auto no-scrollbar pb-0 snap-x snap-mandatory"
        style={{ scrollBehavior: "smooth" }}
      >
        {vendors.map((v, i) => (
          <div
            key={v.id}
            data-vendor-slide
            className={isFeaturedRow
              ? "min-w-[calc(50vw-12px)] max-w-[calc(50vw-12px)] sm:min-w-[280px] sm:max-w-[280px] snap-start"
              : "min-w-[255px] max-w-[255px] sm:min-w-[280px] sm:max-w-[280px]"}
          >
            <VendorCard
              vendor={v}
              index={i}
              onEnquire={onEnquire}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { resetTheme } = useCategoryTheme();
  const { loc } = useLocationCtx();
  const [categories, setCategories] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const eventScroller = useRef(null);

  useEffect(() => {
    const el = eventScroller.current;
    if (!el || eventTypes.length <= 1) return;

    const timer = setInterval(() => {
      const firstCard = el.querySelector("[data-event-slide]");
      if (!firstCard) return;

      const cardWidth = firstCard.getBoundingClientRect().width;
      const gap = 10;
      const maxScroll = el.scrollWidth - el.clientWidth;

      if (el.scrollLeft + cardWidth + gap >= maxScroll - 4) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollBy({
          left: cardWidth + gap,
          behavior: "smooth",
        });
      }
    }, 6500);

    return () => clearInterval(timer);
  }, [eventTypes.length]);
  const [banners, setBanners] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enquireVendor, setEnquireVendor] = useState(null);
  const [homepageConfig, setHomepageConfig] = useState(null);
  const [marketingIndex, setMarketingIndex] = useState(0);
  const [brokenMarketingBanners, setBrokenMarketingBanners] = useState([]);

  useEffect(() => {
    resetTheme();

    if (!loc.city) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const [c, e, b, v, h] = await Promise.all([
          api.get("/categories?include_all=true"),
          api.get("/event-types"),
          api.get(`/banners?city=${encodeURIComponent(loc.city)}`),
          api.get(`/vendors?city=${encodeURIComponent(loc.city)}&limit=48`),
      api.get("/homepage-config").catch(() => ({ data: null })),
        ]);
        setCategories(c.data);
        setEventTypes(e.data);
        setBanners(b.data);
        setVendors(v.data.items);
      setHomepageConfig(h.data);
      } finally { setLoading(false); }
    })();
  }, [resetTheme, loc.city]);

  const marketingBanners = banners.filter(
    (b) => b?.image && !brokenMarketingBanners.includes(b.id)
  );

  useEffect(() => {
    if (marketingIndex >= marketingBanners.length) {
      setMarketingIndex(0);
    }
  }, [marketingBanners.length, marketingIndex]);

  useEffect(() => {
    if (marketingBanners.length <= 1) return;

    const timer = setInterval(() => {
      setMarketingIndex((i) => (i + 1) % marketingBanners.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [marketingBanners.length]);

  const featured = vendors.filter((v) => v.featured);
  const topRated = [...vendors].sort((a, b) => b.rating - a.rating).slice(0, 10);
  const trending = vendors.filter((v) => v.trending);
  const premium = vendors.filter((v) => v.premium);

  const runningText = homepageConfig?.sections?.find(
    (section) =>
      section.section_id === "running-text" &&
      section.enabled !== false
  );

  const runningTextMessages = Array.isArray(runningText?.config?.messages)
    ? runningText.config.messages.filter(Boolean)
    : [];

  const runningTextSpeed =
    runningText?.config?.speed === "slow"
      ? "28s"
      : runningText?.config?.speed === "fast"
        ? "14s"
        : "20s";


  return (
    <Layout>
      {runningTextMessages.length > 0 && (
        <>
          <style>{`
            @keyframes mmep-running-text {
              from { transform: translateX(0); }
              to { transform: translateX(-50%); }
            }

            .mmep-running-text-track {
              animation: mmep-running-text var(--mmep-running-speed) linear infinite;
              width: max-content;
            }

            @media (prefers-reduced-motion: reduce) {
              .mmep-running-text-track {
                animation: none;
                width: 100%;
              }
            }
          `}</style>

          <div
            data-testid="running-text"
            className="w-full overflow-hidden border-y border-purple-100 bg-gradient-to-r from-purple-50 via-white to-fuchsia-50"
          >
            <div className="h-10 sm:h-11 flex items-center overflow-hidden">
              <div
                className="mmep-running-text-track flex items-center whitespace-nowrap"
                style={{ "--mmep-running-speed": runningTextSpeed }}
              >
                {[...runningTextMessages, ...runningTextMessages].map(
                  (message, index) => (
                    <span
                      key={`${index}-${message}`}
                      className="mx-6 sm:mx-8 text-xs sm:text-sm font-semibold text-slate-700"
                    >
                      <span className="mr-3 text-purple-600">✦</span>
                      {message}
                    </span>
                  )
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {!loading && <CategoryBar categories={categories} active="all" />}

      {/* Marketing Carousel */}
      {!loading && marketingBanners.length > 0 && (
        <section
          className="w-full px-2 sm:px-3 py-1"
          data-testid="marketing-carousel"
        >
          <div className="relative w-full overflow-hidden rounded-xl sm:rounded-2xl border-0 shadow-sm bg-muted/20">
            <button
              type="button"
              aria-label="Previous banner"
              onClick={() =>
                setMarketingIndex(
                  (i) => (i - 1 + marketingBanners.length) % marketingBanners.length
                )
              }
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 h-8 w-8 rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/60 transition"
            >
              ‹
            </button>

            <button
              type="button"
              aria-label="Next banner"
              onClick={() =>
                setMarketingIndex((i) => (i + 1) % marketingBanners.length)
              }
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 h-8 w-8 rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/60 transition"
            >
              ›
            </button>

            <div className="relative aspect-[3/1] w-full overflow-hidden">
              <motion.img
                key={marketingBanners[marketingIndex]?.id}
                src={marketingBanners[marketingIndex]?.image}
                alt={marketingBanners[marketingIndex]?.title || "MakeMyEventPro promotion"}
                className="absolute inset-0 w-full h-full object-cover scale-[1.03]"
                initial={{ scale: 1.03 }}
                animate={{ scale: 1.08 }}
                transition={{ duration: 5, ease: "linear" }}
                onError={() => {
                  const failedId = marketingBanners[marketingIndex]?.id;
                  if (failedId) {
                    setBrokenMarketingBanners((ids) =>
                      ids.includes(failedId) ? ids : [...ids, failedId]
                    );
                  }
                }}
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

              <div className="absolute left-4 right-14 bottom-4 text-white">
                {marketingBanners[marketingIndex]?.title && (
                  <div className="font-display font-bold text-lg sm:text-2xl leading-tight drop-shadow-md">
                    {marketingBanners[marketingIndex].title}
                  </div>
                )}

                {marketingBanners[marketingIndex]?.subtitle && (
                  <div className="mt-1 text-xs sm:text-sm text-white/90 line-clamp-1">
                    {marketingBanners[marketingIndex].subtitle}
                  </div>
                )}

                {marketingBanners[marketingIndex]?.cta && (
                  <button
                    type="button"
                    onClick={() => {
                      const link = marketingBanners[marketingIndex]?.link;
                      if (link && link !== "#") navigate(link);
                    }}
                    className="mt-2 inline-flex items-center rounded-full bg-white px-3 py-1.5 text-xs sm:text-sm font-semibold text-purple-700 shadow-md hover:scale-105 transition"
                  >
                    {marketingBanners[marketingIndex].cta}
                    <ChevronRight className="ml-1 h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {marketingBanners.length > 1 && (
                <div className="absolute bottom-2 right-4 flex gap-1.5 z-10">
                  {marketingBanners.map((banner, i) => (
                    <button
                      key={banner.id || i}
                      type="button"
                      aria-label={`Go to banner ${i + 1}`}
                      onClick={() => setMarketingIndex(i)}
                      className={`h-1.5 rounded-full transition-all ${
                        i === marketingIndex
                          ? "w-5 bg-white"
                          : "w-1.5 bg-white/60"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <Row title="Featured Near You" icon={Sparkles} vendors={featured} onEnquire={setEnquireVendor} testid="row-featured" />

      <PopularCategories categories={categories} />

      {/* Explore by Event */}
<section
  className="w-full px-3 sm:px-6 lg:px-8 pt-0 pb-1"
  data-testid="event-types"
>
  <div className="max-w-7xl mx-auto">
    <h2 className="font-display font-bold text-lg sm:text-2xl mb-2">
      Explore by Event
    </h2>

    <div
      ref={eventScroller}
      className="flex gap-2 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-0"
      style={{ scrollBehavior: "smooth" }}
    >
      {eventTypes.map((e, i) => {
        const gradients = [
          "linear-gradient(135deg, #f5edff 0%, #eadcff 100%)",
          "linear-gradient(135deg, #fff1f8 0%, #f9dff0 100%)",
          "linear-gradient(135deg, #eef5ff 0%, #dceaff 100%)",
          "linear-gradient(135deg, #f2f8f4 0%, #dff1e7 100%)",
          "linear-gradient(135deg, #fff7ea 0%, #ffe8c7 100%)",
          "linear-gradient(135deg, #f4f0ff 0%, #e5ddff 100%)",
        ];

        return (
          <motion.button
            key={e.slug}
            data-testid={`event-type-${e.slug}`}
            data-event-slide
            onClick={() =>
              navigate(`/search?event_type=${encodeURIComponent(e.name)}`)
            }
            className="relative shrink-0 snap-start min-w-fit px-5 h-11 rounded-full border border-white/80 shadow-[0_3px_10px_rgba(0,0,0,0.07)] text-[13px] sm:text-sm font-semibold text-slate-700 whitespace-nowrap overflow-hidden"
            style={{
              background: gradients[i % gradients.length],
            }}
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 0.35,
              delay: Math.min(i * 0.04, 0.3),
              ease: "easeOut",
            }}
            whileTap={{ scale: 0.97 }}
          >
            {e.name}
          </motion.button>
        );
      })}
    </div>
  </div>
</section>

{loading ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex gap-4 overflow-hidden">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="min-w-[280px] h-80 rounded-3xl" />)}
        </div>
      ) : (
        <>
          
          <Row title="Trending Near You" icon={TrendingUp} vendors={trending} onEnquire={setEnquireVendor} testid="row-trending" />
          <Row title="Top Rated Vendors" icon={Award} vendors={topRated} onEnquire={setEnquireVendor} testid="row-top-rated" />
          <Row title="Premium Vendors" icon={Star} vendors={premium} onEnquire={setEnquireVendor} testid="row-premium" />
        </>
      )}

      {/* Join as Vendor CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="home-vendor-cta">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white p-8 sm:p-12">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-purple-600/30 blur-3xl" />
          <div className="absolute -left-10 -bottom-10 h-48 w-48 rounded-full bg-pink-500/30 blur-3xl" />
          <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold mb-3"><Store className="h-3.5 w-3.5" /> For event professionals</span>
              <h2 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight">Grow your event business with MakeMyEventPro</h2>
              <p className="text-white/75 mt-2 text-sm sm:text-base">Join thousands of customers looking for trusted vendors. List your business, get quality leads and manage bookings in one place.</p>
            </div>
            <button data-testid="home-join-vendor-btn" onClick={() => navigate("/vendor/join")}
              className="shrink-0 inline-flex items-center gap-2 px-7 h-12 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 font-semibold hover:opacity-90 transition-opacity">
              Join as Vendor <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-500 mt-8" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-white">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-center">How MakeMyEventPro Works</h2>
          <div className="grid sm:grid-cols-4 gap-6 mt-10">
            {[
              { icon: Search, t: "Discover", d: "Find verified vendors near you across 10+ categories." },
              { icon: MessageSquare, t: "Enquire & Compare", d: "Send enquiries, get quotes and compare side-by-side." },
              { icon: ShieldCheck, t: "Book Securely", d: "Confirm bookings with secure payments & clear terms." },
              { icon: CreditCard, t: "Celebrate", d: "Enjoy your event, then share a verified review." },
            ].map((s, i) => (
              <motion.div key={s.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="text-center">
                <div className="h-14 w-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center mx-auto mb-4">
                  <s.icon className="h-6 w-6" />
                </div>
                <div className="font-display font-bold text-lg">{s.t}</div>
                <p className="text-white/80 text-sm mt-1.5">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <EnquiryDialog open={!!enquireVendor} onOpenChange={(o) => !o && setEnquireVendor(null)} vendor={enquireVendor} />
    </Layout>
  );
}
